import { makeStyles } from '@material-ui/styles'
import RadioFields from 'pf9-ui-components/built/components/validatedForm/radio-fields'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { useMemo } from 'react'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import { AddonField } from './ClusterConfigFields'
import { CNI_OPTIONS, type CniFormParams, KUBE_PROXY_MODE_OPTIONS } from './cniTypes'
import SelectableCardWrapper from './SelectableCardWrapper'

export type NetworkingParams = CniFormParams & {
  controlPlaneType?: string
  vip?: string
  networkInterface?: string
  enableProxy?: boolean
  proxyValue?: string
}

interface ClusterNetworkingFieldsProps {
  params: NetworkingParams
  getParamsUpdater: (key: keyof NetworkingParams) => (value: unknown) => void
  isBYOCluster?: boolean
}

// Renders each option's description as muted text aligned under the option's label
const withDescriptions = (
  options: Array<{ value: string; label: string; description: string }>,
  className: string,
) =>
  options.map(({ description, ...option }) => ({
    ...option,
    extraContent: (
      <Text
        variant="body2"
        className={className}
        data-testid={`option-description-${option.value}`}
      >
        {description}
      </Text>
    ),
  }))

export default function ClusterNetworkingFields({
  params,
  getParamsUpdater,
  isBYOCluster = false,
}: ClusterNetworkingFieldsProps) {
  const classes = useStyles()
  const kubeProxyOptions = useMemo(
    () => withDescriptions(KUBE_PROXY_MODE_OPTIONS, classes.optionDescription),
    [classes.optionDescription],
  )
  const isCilium = params?.cni === 'cilium'
  const isCustomControlPlane = params?.controlPlaneType === 'custom'

  // CNF-FR-006: turning the proxy off clears its value so it is not sent with the cluster
  const handleProxyToggle = (enabled: boolean) => {
    getParamsUpdater('enableProxy')(enabled)
    if (!enabled) getParamsUpdater('proxyValue')('')
  }

  return (
    <div className={classes.networkingFields}>
      <div>
        <div className={classes.cniTitle}>
          <Text variant="caption1">Container Network Interface (CNI)</Text>
          {/* CNF-FR-003: immutability, in the CNI title's info tooltip */}
          <HintTooltip
            dataTestId="cni-immutable-info"
            message={
              isCilium
                ? 'The CNI and kube-proxy mode cannot be changed after the cluster is created.'
                : 'The CNI cannot be changed after the cluster is created.'
            }
          />
        </div>
        {/* CNF-FR-001: CNI selection as cards (like the control plane type), details in each card's info */}
        <div className={classes.cniCards}>
          {CNI_OPTIONS.map(({ value, label, info }) => (
            <SelectableCardWrapper
              key={value}
              id={value}
              onClick={getParamsUpdater('cni')}
              active={params?.cni === value}
              title={label}
              description=""
              info={info}
            />
          ))}
        </div>
      </div>
      {/* CNF-FR-002: Cilium-only kube-proxy mode, below the CNI cards like other forms' sub-options */}
      {isCilium && (
        <div data-testid="cilium-options">
          <RadioFields
            id="kubeProxyMode"
            title="Service routing"
            options={kubeProxyOptions}
            value={params?.kubeProxyMode}
            onChange={getParamsUpdater('kubeProxyMode')}
            className={classes.kubeProxyRadios}
          />
        </div>
      )}
      {/* CNF-FR-004 / CNF-FR-005: control plane endpoint for custom BYO control planes */}
      {isCustomControlPlane && isBYOCluster && (
        <>
          <hr className={classes.hr} />
          <div className={classes.vipTextFields}>
            <TextField
              id="vip"
              label="Virtual IP"
              info={
                <Text variant="body2" className={classes.infoText}>
                  An available IP address{' '}
                  <b>from the network on which your control plane hosts reside. </b>
                  kube-vip <b>will advertise it to </b>provide a stable Kubernetes API server
                  endpoint for your cluster.{' '}
                  <b>
                    Ensure that this IP address is reserved for this cluster and not already
                    assigned to another host or part of a DHCP pool.
                  </b>
                </Text>
              }
              value={params?.vip}
              onChange={getParamsUpdater('vip')}
              required
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
            <TextField
              id="nip"
              label="Network Interface"
              info="The name of the network interface on the control plane hosts that kube-vip uses to advertise the virtual IP via ARP (for example, ens3). The interface name must be the same on all control plane hosts."
              value={params?.networkInterface}
              onChange={getParamsUpdater('networkInterface')}
              required
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
          </div>
        </>
      )}
      {/* CNF-FR-006: optional proxy for custom control planes, after the VIP fields */}
      {isCustomControlPlane && (
        <>
          <hr className={classes.hr} />
          <AddonField
            id="enableProxy"
            label="Proxy"
            description="Route the cluster's outbound traffic through a proxy."
            params={params}
            getParamsUpdater={() => handleProxyToggle}
          >
            {params?.enableProxy && (
              <div className={classes.proxyFields}>
                <TextField
                  id="proxy"
                  label="Proxy URL"
                  placeholder="https://username:password@host:port"
                  value={params?.proxyValue}
                  onChange={getParamsUpdater('proxyValue')}
                  required
                  nonce={undefined}
                  onResize={undefined}
                  onResizeCapture={undefined}
                  enterKeyHint={undefined}
                  info="Enter the proxy URL in the format 'http(s)://username:password@host:port'."
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                  crossOrigin={undefined}
                />
              </div>
            )}
          </AddonField>
        </>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  networkingFields: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(3),
  },
  // Radio label text starts 32px in (6px container padding + 8px label padding + 18px text margin)
  optionDescription: {
    marginLeft: 32,
    color: theme.components.typography.passive,
  },
  cniTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  cniCards: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gridGap: theme.spacing(2),
  },
  kubeProxyRadios: {
    display: 'grid',
    gridGap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  hr: {
    height: 1,
    background: theme.components.card.border,
    border: 0,
    margin: theme.spacing(2, 3, 1, 0),
  },
  infoText: {
    color: theme.components.tooltip.text,
  },
  proxyFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  vipTextFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    width: '97%',
  },
}))
